import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_ROLE_COLORS } from '@/theme/chartPalette';
import {
  CHART_AXIS_TICK,
  CHART_LEGEND_STYLE,
  CHART_TOOLTIP_CONTENT_STYLE,
  CHART_TOOLTIP_ITEM_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  DEFAULT_CHART_HEIGHT,
  buildLabelFormatter,
  buildValueFormatter,
} from './chartStyles';

export interface MultiSeriesBarSeries {
  key: string;
  label: string;
  color: string;
}

interface MultiSeriesBarChartProps {
  data: Array<Record<string, string | number>>;
  xKey: string;
  series: MultiSeriesBarSeries[];
  height?: number;
  accessibleLabel: string;
  emptyLabel: string;
  formatXTick: (value: string) => string;
  formatValue: (value: number) => string;
}

export function MultiSeriesBarChart({
  data,
  xKey,
  series,
  height = DEFAULT_CHART_HEIGHT,
  accessibleLabel,
  emptyLabel,
  formatXTick,
  formatValue,
}: MultiSeriesBarChartProps) {
  if (data.length === 0 || series.length === 0) {
    return <p className="text-xs text-zoomuted italic py-6 text-center">{emptyLabel}</p>;
  }
  return (
    <div role="img" aria-label={accessibleLabel} className="w-full">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={CHART_ROLE_COLORS.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey={xKey}
            tickFormatter={formatXTick}
            tick={CHART_AXIS_TICK}
            axisLine={{ stroke: CHART_ROLE_COLORS.grid }}
            tickLine={false}
            interval={0}
          />
          <YAxis
            tickFormatter={formatValue}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            width={36}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: CHART_ROLE_COLORS.grid, fillOpacity: 0.35 }}
            formatter={buildValueFormatter(formatValue)}
            labelFormatter={buildLabelFormatter(formatXTick)}
            contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
            labelStyle={CHART_TOOLTIP_LABEL_STYLE}
            itemStyle={CHART_TOOLTIP_ITEM_STYLE}
          />
          <Legend wrapperStyle={CHART_LEGEND_STYLE} iconType="circle" iconSize={9} />
          {series.map(function renderBar(entry) {
            return (
              <Bar
                key={entry.key}
                dataKey={entry.key}
                name={entry.label}
                fill={entry.color}
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
